import pluralize from "pluralize";
import { PlayerCardReviewDisplayType, PlayerCardWidgetOwner } from "../../services/bedrock";
import _ from "lodash";
import { SOURCE_FILTER_LABELS } from "@/components/reviews/ReviewSourceFilterButtons";
import {
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  BrainCircuit,
  ContactRound,
  FilePlay,
  Link,
  Mail,
  MapPinHouse,
  Newspaper,
  Phone,
  Share2,
  SignpostBig,
  Star,
} from "lucide-react";

export const REVIEW_DISPLAY_TYPE_LABELS: Record<PlayerCardReviewDisplayType, string> = {
  FEATURED_REVIEWS: "Featured reviews",
  SPECIFIC_REVIEW: "Specific review",
  STAR_RATING: "Star rating",
  NPS_RATING: "NPS rating",
  SATISFACTION_RATING: "Customer satisfaction rating",
};

export interface ReviewMinScoreSetting {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  suffix: string;
}

export const REVIEW_MIN_SCORE_SETTINGS: Partial<Record<PlayerCardReviewDisplayType, ReviewMinScoreSetting>> = {
  STAR_RATING: { min: 1, max: 5, step: 0.1, defaultValue: 4, suffix: " stars" },
  NPS_RATING: { min: -100, max: 100, step: 1, defaultValue: 50, suffix: "" },
  SATISFACTION_RATING: { min: 0, max: 100, step: 1, defaultValue: 70, suffix: "%" },
};

export const getCountMetricsText = (num: number, metricType?: string) => {
  var countText = num.toString();
  if (num >= 1000000) {
    countText = (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1000) {
    countText = (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  if (metricType) {
    return `${countText} ${pluralize(metricType, num)}`;
  } else {
    return countText;
  }
};

export const getWidgetTitle = (widgetType: string) => {
  return (
    {
      ContentPage: "Video Page",
      SocialMedia: "Social Media",
      SmartLink: "Smart Link",
      Review: "Reviews",
    }[widgetType] || widgetType
  );
};

export const getWidgetSubtitle = (widgetOwner: PlayerCardWidgetOwner) => {
  if (widgetOwner.type === "Link") {
    return widgetOwner.url;
  } else if (widgetOwner.type === "ContentPage" || widgetOwner.type === "Blog") {
    return _.startCase(_.capitalize(widgetOwner.selectionType));
  } else if (widgetOwner.type === "Review") {
    const label = REVIEW_DISPLAY_TYPE_LABELS[widgetOwner.displayType];
    if (widgetOwner.displayType === "FEATURED_REVIEWS" && widgetOwner.sourceSystems?.length) {
      return `${label} (${widgetOwner.sourceSystems.map(source => SOURCE_FILTER_LABELS[source]).join(", ")})`;
    }
    const minScoreSetting = REVIEW_MIN_SCORE_SETTINGS[widgetOwner.displayType];
    if (!minScoreSetting || widgetOwner.minScore == null) return label;
    return `${label} (min ${widgetOwner.minScore}${minScoreSetting.suffix})`;
  } else if (widgetOwner.type === "Phone") {
    return widgetOwner.phoneNumber;
  } else if (widgetOwner.type === "Email") {
    return widgetOwner.email;
  } else if (widgetOwner.type === "Address") {
    return [
      [widgetOwner.streetLine1, widgetOwner.streetLine2].filter(Boolean).join(" "),
      widgetOwner.city,
      [widgetOwner.state, widgetOwner.zip].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");
  } else {
    return null;
  }
};

export const getWidgetIcon = (widgetOwner: PlayerCardWidgetOwner): React.ElementType => {
  const iconMapping: Record<PlayerCardWidgetOwner["type"], React.ElementType> = {
    Link: Link,
    SocialMedia: Share2,
    ContentPage: FilePlay,
    Blog: Newspaper,
    Post: SignpostBig,
    SmartLink: BrainCircuit,
    Phone: Phone,
    Email: Mail,
    Address: MapPinHouse,
    Contact: ContactRound,
    Review: Star,
    Header: AlignVerticalJustifyStart,
    Footer: AlignVerticalJustifyEnd,
  };
  return iconMapping[widgetOwner.type];
};
